import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';

interface Props {
  open: boolean;
  phoneDisplay: string;
  code: string;
  onCodeChange: (digits: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  onResend: () => void;
  confirming: boolean;
  resending: boolean;
  resendSecondsLeft: number;
  modalError?: string;
}

function formatMmSs(totalSeconds: number): string {
  const m = Math.floor(Math.max(0, totalSeconds) / 60);
  const s = Math.max(0, totalSeconds) % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const MobileOtpModal: React.FC<Props> = ({
  open,
  phoneDisplay,
  code,
  onCodeChange,
  onClose,
  onConfirm,
  onResend,
  confirming,
  resending,
  resendSecondsLeft,
  modalError,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 80);
    return () => window.clearTimeout(t);
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/45 p-4">
      <div className="w-full max-w-md rounded-2xl border bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-lg font-bold">تایید شماره موبایل</h2>
            <p className="mt-1 text-xs text-gray-600">
              کد ۵ رقمی ارسال‌شده به <span dir="ltr" className="font-mono">{phoneDisplay}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 hover:bg-gray-100">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-3 px-5 py-4">
          {modalError ? (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{modalError}</div>
          ) : null}
          <input
            ref={inputRef}
            value={code}
            onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, '').slice(0, 5))}
            maxLength={5}
            dir="ltr"
            className="w-full rounded-xl border px-3 py-2 text-center font-mono text-2xl tracking-[0.35em]"
            placeholder="....."
          />
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={onResend}
              disabled={resendSecondsLeft > 0 || resending}
              className="text-sm text-emerald-700 disabled:text-gray-400"
            >
              {resending ? 'در حال ارسال...' : resendSecondsLeft > 0 ? `ارسال مجدد تا ${formatMmSs(resendSecondsLeft)}` : 'ارسال مجدد'}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={code.replace(/\D/g, '').length !== 5 || confirming}
              className="inline-flex items-center gap-2 rounded-xl bg-[#0a4d29] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {confirming ? <Loader2 size={15} className="animate-spin" /> : null}
              تایید
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default MobileOtpModal;
